import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Form, Skeleton } from 'antd';

import { routeFileSelector } from '@/utilz';
import type { RootState } from '@/redux/RootReducer';
import SaveCancelButtons from '@/views/components/Buttons/SaveCancelButtons';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import Paper from '@/views/components/Paper/Paper';

import { useGetCarrierInformationSettings, useUpdateCarrierInformationSettings } from '../../hooks';
import type { CarrierInformationSettings } from '../../types';
import ClaimsSettingsCarrierForm from './carrier-form/claims-settings-carrier-form';

interface ClaimsSettingsCarrierInformationFormProps {
  carrierKey?: number;
}

const ClaimsSettingsCarrierInformation: FC<ClaimsSettingsCarrierInformationFormProps> = ({ carrierKey }) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm<CarrierInformationSettings>();
  const { selectedCompany } = useSelector((state: RootState) => state.auth);

  const { data: carrierInfo, isFetching: isLoading } = useGetCarrierInformationSettings({
    carrierKey,
    companyId: selectedCompany?.companyId,
  });

  const { mutate: saveSettings } = useUpdateCarrierInformationSettings({ companyId: selectedCompany?.companyId });

  const handleSubmit = () => {
    form.submit();
  };

  const handleSave = (formValues: CarrierInformationSettings) => {
    saveSettings(formValues);
  };

  const resetForm = () => {
    form.setFieldsValue(carrierInfo as CarrierInformationSettings);
  };

  useEffect(() => {
    if (carrierInfo) {
      form.setFieldsValue(carrierInfo);
    }
  }, [carrierInfo, form]);

  return (
    <Paper className="d-flex flex-direction-column flex-nowrap" border>
      <div className="px-15 py-10 border-bottom-border d-flex justify-content-between align-items-center">
        <PrimaryLabel>Carrier Information</PrimaryLabel>
        {Boolean(carrierKey) && !isLoading && (
          <SaveCancelButtons
            onCancel={resetForm}
            onSave={handleSubmit}
            cancelText={t('translation:default.buttons.restoreToDefault')}
          />
        )}
      </div>
      <div className="px-15 pt-10 pb-10">
        <Form<CarrierInformationSettings> onFinish={handleSave} form={form}>
          {isLoading && <Skeleton />}
          {!isLoading &&
            (carrierKey ? (
              <ClaimsSettingsCarrierForm form={form} />
            ) : (
              <EmptyRecords descriptionOne="" descriptionTwo="" title="Please select a carrier" />
            ))}
        </Form>
      </div>
    </Paper>
  );
};

export default ClaimsSettingsCarrierInformation;
